import React, { ComponentType, FC, useEffect } from "react";

import ElectricityDataProvider from "../../../../providers/ElectricityDataProvider";
import GasDataProvider from "../../../../providers/GasDataProvider";
import HeatingOilDataProvider from "../../../../providers/HeatingOilDataProvider";
import { useIsWebview } from "../../../../providers/IsWebviewProvider";
import PelletsDataProvider from "../../../../providers/PelletsDataProvider";
import ElectricityCalculatorPage from "../../../pages/Electricity/CalculatorPage";
import GasCalculatorPage from "../../../pages/Gas/CalculatorPage";
import HeatingOilCalculatorPage from "../../../pages/HeatingOil/CalculatorPage";
import PelletsCalculatorPage from "../../../pages/Pellets/CalculatorPage";
import {
    PRODUCT_GAS,
    PRODUCT_STROM,
    RESPONSIBLE_COMPANY_HOYER_GMBH,
    RESPONSIBLE_COMPANY_HOYER_STROM_GAS,
    usePageData,
} from "../../Page";

export enum CalculatorWithOutputType {
    electricity = "electricity",
    gas = "gas",
    heatingOil = "heatingOil",
    pellets = "pellets",
}

type SpecificCalculatorProps = {
    isWebview: boolean;
    isActive: boolean;
    inactiveText: string;
};

type Product = typeof PRODUCT_STROM | typeof PRODUCT_GAS | null;
type ResponsibleCompany =
    | typeof RESPONSIBLE_COMPANY_HOYER_GMBH
    | typeof RESPONSIBLE_COMPANY_HOYER_STROM_GAS;
const calculatorTypes: Record<
    CalculatorWithOutputType,
    {
        responsibleCompany: ResponsibleCompany;
        product: Product;
        Component: ComponentType<SpecificCalculatorProps>;
    }
> = {
    [CalculatorWithOutputType.electricity]: {
        Component: (props) => (
            <ElectricityDataProvider>
                <ElectricityCalculatorPage {...props} />
            </ElectricityDataProvider>
        ),
        responsibleCompany: RESPONSIBLE_COMPANY_HOYER_STROM_GAS,
        product: PRODUCT_STROM,
    },
    [CalculatorWithOutputType.gas]: {
        Component: (props) => (
            <GasDataProvider>
                <GasCalculatorPage {...props} />
            </GasDataProvider>
        ),
        responsibleCompany: RESPONSIBLE_COMPANY_HOYER_STROM_GAS,
        product: PRODUCT_GAS,
    },
    [CalculatorWithOutputType.heatingOil]: {
        Component: (props) => (
            <HeatingOilDataProvider>
                <HeatingOilCalculatorPage {...props} />
            </HeatingOilDataProvider>
        ),
        responsibleCompany: RESPONSIBLE_COMPANY_HOYER_GMBH,
        product: null,
    },
    [CalculatorWithOutputType.pellets]: {
        Component: (props) => (
            <PelletsDataProvider>
                <PelletsCalculatorPage {...props} />
            </PelletsDataProvider>
        ),
        responsibleCompany: RESPONSIBLE_COMPANY_HOYER_GMBH,
        product: null,
    },
};

export type CalculatorWithOutputProps = Omit<
    SpecificCalculatorProps,
    "isWebview"
> & {
    calcType: CalculatorWithOutputType;
};

const CalculatorWithOutput: FC<CalculatorWithOutputProps> = ({
    calcType,
    ...props
}) => {
    const { Component, product, responsibleCompany } =
        calculatorTypes[calcType];
    const isWebview = useIsWebview();

    const { setResponsibleCompany, setProduct } = usePageData();

    useEffect(() => {
        setProduct(product);
        setResponsibleCompany(responsibleCompany);
    }, [setProduct, setResponsibleCompany, product, responsibleCompany]);

    return <Component {...{ isWebview, ...props }} />;
};
export default CalculatorWithOutput;
